import { Link } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";

interface ArticleLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  breadcrumbLabel: string;
  breadcrumbTo: string;
  relatedArticles: { label: string; to: string }[];
}

const SITE_URL = "https://hipobuyspread.cfd";

export default function ArticleLayout({
  title,
  subtitle,
  children,
  breadcrumbLabel,
  breadcrumbTo,
  relatedArticles,
}: ArticleLayoutProps) {
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: subtitle || title,
    url: `${SITE_URL}${breadcrumbTo}`,
    datePublished: "2026-07-12",
    dateModified: "2026-07-12",
    author: {
      "@type": "Organization",
      name: "Hipobuy Spreadsheet",
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "Hipobuy Spreadsheet",
      url: SITE_URL,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}${breadcrumbTo}`,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleJsonLd} />
      {/* Compact Nav */}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2 cursor-pointer">
              <div className="w-7 h-7 rounded-md bg-primary-500 flex items-center justify-center">
                <i className="ri-grid-fill text-background-50 text-sm"></i>
              </div>
              <span className="font-heading text-sm font-bold text-foreground-950 whitespace-nowrap">
                Hipobuy Spreadsheet
              </span>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/hipobuy-spreadsheet-guide" className="text-xs md:text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                Guide
              </Link>
              <Link to="/hipobuy-spreadsheet-safety" className="text-xs md:text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                Safety
              </Link>
              <Link to="/" className="text-xs md:text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                Home
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <article className="px-4 md:px-6 max-w-[880px] mx-auto py-10 md:py-16">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-8">
          <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <Link to={breadcrumbTo} className="text-foreground-700 font-medium">{breadcrumbLabel}</Link>
        </nav>

        {/* Title */}
        <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
          {title}
        </h1>
        {subtitle && (
          <p className="text-base md:text-lg text-foreground-600 mb-10 leading-relaxed">
            {subtitle}
          </p>
        )}

        {/* Article Body */}
        <div className="prose prose-base md:prose-lg max-w-none text-foreground-700 space-y-6 leading-relaxed">
          {children}
        </div>

        {/* Back to Home */}
        <div className="mt-14 pt-8 border-t border-background-200/70">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Homepage
          </Link>
        </div>

        {/* Related Articles */}
        <div className="mt-10">
          <h3 className="text-sm font-semibold text-foreground-800 uppercase tracking-wider mb-4">
            Related Articles
          </h3>
          <div className="flex flex-wrap gap-3">
            {relatedArticles.map((article) => (
              <Link
                key={article.to}
                to={article.to}
                className="px-4 py-2 bg-background-100 border border-background-200/70 text-sm text-foreground-700 rounded-full hover:border-primary-300 hover:text-primary-500 transition-all cursor-pointer"
              >
                {article.label}
              </Link>
            ))}
          </div>
        </div>
      </article>

      {/* Compact Footer */}
      <footer className="border-t border-background-200/70 py-6">
        <div className="px-4 md:px-6 max-w-[880px] mx-auto text-center">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} Hipobuy Spreadsheet. Educational resource — not a substitute for due diligence.
          </p>
        </div>
      </footer>
    </div>
  );
}